import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useSceneStore } from "@/state/useSceneStore";
import { useFps } from "@/hooks/useFps";
import type { ObjectName } from "@/types";

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

const mapFpsToScale = (fps: number): number =>
  clamp(0.4 + (fps - 30) / 60, 0.4, 1.4);

const NAME_MAP: Partial<Record<string, ObjectName>> = {
  Book: "book",
  Stand: "monitor",
  SwitchBoard: "switch",
  Rubik_Cube: "cube",
  Keyboard: "keyboard",
  Plant_Pot: "plant",
  CPU: "cpu",
  Mobile: "phone",
  Coffe: "mug",
};

const FAN_CLIPS = [
  "fan_rotation",
  "fan_rotation.001",
  "fan_rotation.002",
  "fan_rotation.003",
  "fan_rotation.004",
];

export const Room = (): JSX.Element => {
  const gltf = useGLTF("/models/room.optimized.glb");
  const register = useSceneStore((s) => s.registerObject);
  const setCanvasReady = useSceneStore((s) => s.setCanvasReady);

  const mixer = useRef<THREE.AnimationMixer | null>(null);
  const clock = useRef<THREE.Clock>(new THREE.Clock());
  const fps = useFps();

  useEffect(() => {
    gltf.scene.traverse((child) => {
      const mapped = NAME_MAP[child.name];
      if (mapped) register(mapped, child);
      if (child.name === "Stand" && child.children[0]) {
        register("monitorScreen", child.children[0]);
      }
      if (child.name !== "Wall") child.castShadow = true;
      child.receiveShadow = true;
    });
    setCanvasReady(true);
  }, [gltf, register, setCanvasReady]);

  useEffect(() => {
    const m = new THREE.AnimationMixer(gltf.scene);
    mixer.current = m;
    FAN_CLIPS.forEach((name) => {
      const clip = THREE.AnimationClip.findByName(gltf.animations, name);
      if (clip) m.clipAction(clip).play();
    });
    return () => {
      m.stopAllAction();
      mixer.current = null;
    };
  }, [gltf]);

  useFrame(() => {
    const delta = clock.current.getDelta();
    const scale = mapFpsToScale(fps);
    mixer.current?.update(delta * scale);
  });

  return <primitive object={gltf.scene} />;
};

useGLTF.preload("/models/room.optimized.glb");
